import React, { useEffect, useMemo, useState } from "react";
import { Mic2, Save, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const getDisplayName = (profile) => profile?.custom_name || profile?.suggested_name || profile?.label || profile?.speaker_id;

export const SpeakerManagementDialog = ({
  open,
  onOpenChange,
  profiles = [],
  language = "es",
  onSaveSpeaker,
  onMergeSpeaker,
  onDeleteSpeaker,
  savingSpeakerId = "",
  mergingSpeakerId = "",
  deletingSpeakerId = "",
}) => {
  const [drafts, setDrafts] = useState({});
  const [mergeTargets, setMergeTargets] = useState({});

  useEffect(() => {
    if (!open) return;
    const nextDrafts = {};
    profiles.forEach((profile) => {
      nextDrafts[profile.speaker_id] = profile.custom_name || profile.suggested_name || profile.label || "";
    });
    setDrafts(nextDrafts);
    const nextMergeTargets = {};
    profiles.forEach((profile) => {
      const target = profiles.find((candidate) => candidate.speaker_id !== profile.speaker_id);
      nextMergeTargets[profile.speaker_id] = target?.speaker_id || "";
    });
    setMergeTargets(nextMergeTargets);
  }, [open, profiles]);

  const copy = useMemo(() => language === "es" ? {
    title: "Gestionar speakers",
    description: "Puedes dejar Speaker 1 / Speaker 2 o renombrarlos manualmente. El cambio se reflejará en transcripción, momentos y exportaciones PDF/Word.",
    suggested: "Sugerencia IA",
    sample: "Muestra",
    inputLabel: "Nombre visible",
    save: "Guardar",
    delete: "Borrar",
    mergeInto: "Fusionar dentro de",
    merge: "Fusionar",
  } : {
    title: "Manage speakers",
    description: "Keep Speaker 1 / Speaker 2 or rename them manually. Changes will reflect across transcript, moments, and PDF/Word exports.",
    suggested: "AI suggestion",
    sample: "Sample",
    inputLabel: "Visible name",
    save: "Save",
    delete: "Delete",
    mergeInto: "Merge into",
    merge: "Merge",
  }, [language]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl" data-testid="speaker-management-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2" data-testid="speaker-management-dialog-title">
            <Mic2 className="h-4 w-4 text-primary" />
            {copy.title}
          </DialogTitle>
          <DialogDescription data-testid="speaker-management-dialog-description">{copy.description}</DialogDescription>
        </DialogHeader>

        <div className="max-h-[70vh] space-y-4 overflow-y-auto pr-1" data-testid="speaker-management-dialog-list">
          {profiles.map((profile) => {
            const currentValue = drafts[profile.speaker_id] ?? "";
            return (
              <div key={profile.speaker_id} className="rounded-2xl border border-border bg-muted/20 p-4" data-testid={`speaker-management-card-${profile.speaker_id}`}>
                <div className="mb-3 flex flex-wrap items-center gap-2">
                  <span className="rounded-full border border-primary/30 bg-primary/10 px-3 py-1 text-xs font-medium text-primary" data-testid={`speaker-management-label-${profile.speaker_id}`}>
                    {profile.label}
                  </span>
                  <span className="text-sm font-medium text-foreground" data-testid={`speaker-management-current-name-${profile.speaker_id}`}>
                    {getDisplayName(profile)}
                  </span>
                  {profile.suggested_name ? (
                    <span className="rounded-full border border-emerald-500/25 bg-emerald-500/10 px-2.5 py-1 text-[11px] text-emerald-300" data-testid={`speaker-management-suggestion-${profile.speaker_id}`}>
                      {copy.suggested}: {profile.suggested_name}
                    </span>
                  ) : null}
                </div>

                {profile.sample_quote ? (
                  <p className="mb-4 border-l border-primary/30 pl-3 text-sm italic leading-6 text-muted-foreground" data-testid={`speaker-management-sample-${profile.speaker_id}`}>
                    <span className="font-medium not-italic text-foreground">{copy.sample}:</span> “{profile.sample_quote}”
                  </p>
                ) : null}

                <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
                  <div className="flex-1 space-y-2">
                    <label className="text-sm font-medium" htmlFor={`speaker-name-${profile.speaker_id}`}>{copy.inputLabel}</label>
                    <Input
                      id={`speaker-name-${profile.speaker_id}`}
                      value={currentValue}
                      onChange={(event) => setDrafts((prev) => ({ ...prev, [profile.speaker_id]: event.target.value }))}
                      data-testid={`speaker-management-input-${profile.speaker_id}`}
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      onClick={() => onSaveSpeaker?.(profile.speaker_id, currentValue)}
                      disabled={savingSpeakerId === profile.speaker_id}
                      data-testid={`speaker-management-save-${profile.speaker_id}`}
                    >
                      <Save className="mr-2 h-4 w-4" />
                      {copy.save}
                    </Button>
                    <Button
                      type="button"
                      variant="destructive"
                      onClick={() => onDeleteSpeaker?.(profile.speaker_id)}
                      disabled={deletingSpeakerId === profile.speaker_id}
                      data-testid={`speaker-management-delete-${profile.speaker_id}`}
                    >
                      <Trash2 className="mr-2 h-4 w-4" />
                      {copy.delete}
                    </Button>
                  </div>
                </div>

                {profiles.length > 1 ? (
                  <div className="mt-4 flex flex-col gap-3 rounded-xl border border-border/60 bg-background/60 p-3 sm:flex-row sm:items-end">
                    <div className="flex-1 space-y-2">
                      <label className="text-sm font-medium" htmlFor={`speaker-merge-${profile.speaker_id}`}>{copy.mergeInto}</label>
                      <select
                        id={`speaker-merge-${profile.speaker_id}`}
                        className="w-full rounded-xl border border-border bg-background px-3 py-2 text-sm"
                        value={mergeTargets[profile.speaker_id] || ""}
                        onChange={(event) => setMergeTargets((prev) => ({ ...prev, [profile.speaker_id]: event.target.value }))}
                        data-testid={`speaker-management-merge-select-${profile.speaker_id}`}
                      >
                        {profiles.filter((candidate) => candidate.speaker_id !== profile.speaker_id).map((candidate) => (
                          <option key={candidate.speaker_id} value={candidate.speaker_id}>
                            {getDisplayName(candidate)}
                          </option>
                        ))}
                      </select>
                    </div>
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={() => onMergeSpeaker?.(profile.speaker_id, mergeTargets[profile.speaker_id])}
                      disabled={!mergeTargets[profile.speaker_id] || mergingSpeakerId === profile.speaker_id}
                      data-testid={`speaker-management-merge-${profile.speaker_id}`}
                    >
                      {copy.merge}
                    </Button>
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
};